import type {
  ProductionCommentAccess,
  RfqProject,
  RfqSubmission,
  SupplierWorkFlag,
  TimberProject,
  VendorCapability,
  VendorWorkType,
} from '../types';
import {
  buildProductionCommentAccessDoc,
  dealIdFromProductionCommentKey,
  dealIsUnderContract,
  productionCommentAccessId,
} from './productionCommentAccess';

export type VendorPortalCommentMode = 'supplier' | 'staff-preview' | 'embed';

export function resolveVendorPortalAccessDoc(input: {
  dealId: string;
  storedAccess?: ProductionCommentAccess[];
  rfqProjects: RfqProject[];
  submissions: Array<Pick<RfqSubmission, 'id' | 'supplierOrgId' | 'dealId' | 'rfqProjectId'>>;
  hubProjects?: Array<Pick<TimberProject, 'id' | 'dealId' | 'phase'>>;
  /** Real suppliers must use the denormalized ACL doc — no RFQ fallback. */
  requireStored?: boolean;
}): ProductionCommentAccess {
  const dealId = input.dealId.trim();
  const stored = input.storedAccess?.find(
    (row) => row.dealId === dealId || row.id === productionCommentAccessId(dealId),
  );
  if (stored) return stored;
  if (input.requireStored) {
    return { dealId, timberOrgIds: [], cncOrgIds: [], underContract: false };
  }
  return buildProductionCommentAccessDoc({
    dealId,
    projects: input.rfqProjects,
    submissions: input.submissions,
    hubProjects: input.hubProjects,
  });
}

export function vendorPortalShowProductionComments(input: {
  workType?: VendorWorkType | 'timber' | 'cnc' | null;
  orgId?: string;
  flags?: SupplierWorkFlag[] | unknown;
  /** Kept for call-site compatibility; awards gate access, not designations. */
  orgCapabilities?: VendorCapability[] | unknown;
  access?: Pick<ProductionCommentAccess, 'timberOrgIds' | 'cncOrgIds'> | null;
  mode: VendorPortalCommentMode;
}): boolean {
  if (input.mode === 'embed') return false;
  const orgId = input.orgId?.trim();
  const access = input.access;
  if (!orgId || !access) return false;
  const isCnc = input.workType === 'cnc_quote' || input.workType === 'cnc';
  // Production is one shared deal thread — award on that work type is enough
  // (no designation/capability gate; employees share the org award).
  return isCnc ? access.cncOrgIds.includes(orgId) : access.timberOrgIds.includes(orgId);
}

export function vendorPortalDealUnderContract(input: {
  dealId: string;
  storedAccess?: Pick<ProductionCommentAccess, 'underContract'> | null;
  hubProjects?: Array<Pick<TimberProject, 'dealId' | 'phase'>>;
  mode: VendorPortalCommentMode;
}): boolean {
  if (input.mode === 'embed') return false;
  if (input.mode === 'supplier') return input.storedAccess?.underContract === true;
  return dealIsUnderContract(input.hubProjects ?? [], input.dealId);
}

export function vendorPortalDeepLinkDealId(input: {
  dealParam?: string | null;
  groupParam?: string | null;
}): string {
  const fromDeal = input.dealParam?.trim() || '';
  if (fromDeal) return fromDeal;
  return dealIdFromProductionCommentKey(input.groupParam || undefined);
}
